import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import Skeleton from 'react-loading-skeleton'

import {
  ChevronRightIcon,
  CloseCircleIcon,
  SearchIcon
} from '@toasttab/buffet-pui-icons'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

import { DigitalSurface } from 'src/apollo/onlineOrdering'

import useGeocoder from 'shared/components/common/location_search/useGeocoder'
import { Modal, ModalOverlay } from 'src/shared/components/common/modal/Modal'
import { useModal } from 'src/shared/components/common/modal/useModal'
import Button from 'src/shared/components/common/button'

import {
  normalizeAddressString,
  hasPostalCode
} from 'public/components/online_ordering/addressUtils'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { AuthenticationSource } from 'src/public/components/default_template/online_ordering/checkout/checkoutUtils'
import { PwlessAuth } from 'src/public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { t } from 'src/translations'
import { normalizeGoogleAddress } from 'src/public/components/online_ordering/addressUtils'
import { normalizeGoogleAddress as normalizeGoogleAddressWithConfigs } from 'src/public/components/online_ordering/address_configs'
import { useOptionalRestaurant } from '../restaurant_context/RestaurantContext'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { ButtonType } from 'src/apollo/sites'
import {
  A11yProvider,
  useA11y
} from 'src/public/components/online_ordering/useA11y'

const MINIMUM_AUTOCOMPLETE_CHARS = 3
const LOCATION_BIAS_RADIUS = 10
const PLACES_API_DEBOUNCE_MS = 1000

const DEFAULT_COUNTRY_US = 'us'

type Props = {
  id: string
  defaultValue?: string
  placeholder?: string
  autoFocus?: boolean
  apiKey: string
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void
  locationBias?: { lat: number; long: number }
  onValueChange?: (value: string) => void
  normalized?: boolean
  requireZip?: boolean
  onError?: () => void
  showGenericSpinner?: boolean
  source?: DigitalSurface
  showManualButton?: boolean
  triggerManualAddress?: (searchText?: string) => void
  helperText: string
}

const InlinePlacesAutocomplete = ({
  id,
  defaultValue,
  placeholder = t('inline-places-autocomplete.enter-city'),
  apiKey,
  onPlaceSelected,
  autoFocus = false,
  locationBias,
  onValueChange = () => {},
  normalized = false,
  requireZip = false,
  onError,
  source,
  triggerManualAddress,
  helperText
}: Props) => {
  const { placePredictions, getPlacePredictions, isPlacePredictionsLoading } =
    usePlacesService({
      apiKey: apiKey,
      libraries: ['places', 'geocoding'],
      debounce: PLACES_API_DEBOUNCE_MS
    })
  const geocoder = useGeocoder()
  const { customer } = useCustomer()
  const { isOpen, onOpen, onClose } = useModal()
  const tracker = useTracker()
  const ooRestaurant = useOptionalRestaurant()?.ooRestaurant
  const [searchText, setSearchText] = useState<string>()
  const predictionsRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [hasSearchedDefaultValue, setHasSearchedDefaultValue] = useState(false)
  const { intlAddressCountryLogicErrorHandling } = useFlags()
  const shouldShowManualButton =
    intlAddressCountryLogicErrorHandling && source !== DigitalSurface.Local
  const shouldShowNoResultsAndManualButton =
    shouldShowManualButton &&
    !isPlacePredictionsLoading &&
    (!placePredictions || placePredictions.length === 0) &&
    searchText
  const { announce } = useA11y()

  const onSearchChange = useCallback(
    (value: string) => {
      onValueChange(value || '')
      setSearchText(value)

      const rxCountry = (
        source === DigitalSurface.Local
          ? DEFAULT_COUNTRY_US
          : ooRestaurant?.i18n?.country
      )?.toLowerCase()

      // Only get autocomplete predictions if we have three characters to increase the likelihood of a match
      if (value.length >= MINIMUM_AUTOCOMPLETE_CHARS) {
        getPlacePredictions({
          input: value,
          types: ['geocode'],
          componentRestrictions: rxCountry ? { country: rxCountry } : undefined,
          radius: locationBias ? LOCATION_BIAS_RADIUS : undefined,
          location: locationBias
            ? new google.maps.LatLng({
                lat: locationBias.lat,
                lng: locationBias.long
              })
            : undefined
        })
      }
    },
    [
      onValueChange,
      source,
      ooRestaurant?.i18n?.country,
      getPlacePredictions,
      locationBias
    ]
  )
  const waitForGoogle = () => {
    return new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (window.google) {
          clearInterval(intervalId)
          resolve(window.google)
        }
      }, 200)
    })
  }

  const handleDefaultValue = useCallback(async () => {
    await waitForGoogle()
    if (defaultValue && !hasSearchedDefaultValue) {
      setHasSearchedDefaultValue(true)
      onSearchChange(defaultValue)
    }
  }, [defaultValue, onSearchChange, hasSearchedDefaultValue])

  useEffect(() => {
    handleDefaultValue()
  }, [handleDefaultValue])

  const searchOnGeocodedRequest = useCallback(
    (request: google.maps.GeocoderRequest) => {
      geocoder().geocode(request, (result) => {
        const place = result?.[0]
        if (place) {
          if (source === DigitalSurface.Local) {
            const countryComponent = place.address_components?.find(
              (component) => component.types.includes('country')
            )
            if (
              countryComponent &&
              countryComponent.short_name.toLowerCase() !== DEFAULT_COUNTRY_US
            ) {
              return
            }
          }

          if (requireZip) {
            const normalizedAddress = intlAddressCountryLogicErrorHandling
              ? normalizeGoogleAddressWithConfigs(
                  place,
                  ooRestaurant?.i18n?.country
                )
              : normalizeGoogleAddress(place)
            if (normalizedAddress?.zipCode) {
              onPlaceSelected(place)
            } else {
              // retry using the address input if a zip code is required and not found with the first request
              geocoder().geocode({ address: searchText }, (retryResult) => {
                if (retryResult?.[0]) {
                  const normalizedAddress = intlAddressCountryLogicErrorHandling
                    ? normalizeGoogleAddressWithConfigs(
                        retryResult[0],
                        ooRestaurant?.i18n?.country
                      )
                    : normalizeGoogleAddress(retryResult[0])
                  if (normalizedAddress?.zipCode) {
                    onPlaceSelected(retryResult?.[0])
                  } else {
                    onError?.()
                  }
                }
              })
            }
          } else {
            onPlaceSelected(place)
          }

          if (normalized) {
            const normalizedAddress = intlAddressCountryLogicErrorHandling
              ? normalizeGoogleAddressWithConfigs(
                  place,
                  ooRestaurant?.i18n?.country
                )
              : normalizeGoogleAddress(place)
            if (normalizedAddress) {
              const addressString = normalizeAddressString(normalizedAddress)
              setSearchText(addressString)
            }
          }
        }
      })
    },
    [
      geocoder,
      source,
      requireZip,
      normalized,
      intlAddressCountryLogicErrorHandling,
      ooRestaurant?.i18n?.country,
      onPlaceSelected,
      searchText,
      onError
    ]
  )

  const onPredictionClick = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) => {
      setSearchText(
        `${prediction.structured_formatting.main_text}, ${prediction.structured_formatting.secondary_text}`
      )
      const address = prediction.description
      const placeId = prediction.place_id
      // if we have a prediction with a valid postalCode, great, lets use that placeID!
      // if not, lets use the address string so we can do our best to match the user input location with google
      if (requireZip && hasPostalCode(prediction)) {
        searchOnGeocodedRequest({ placeId })
      } else {
        searchOnGeocodedRequest({ address })
      }
      inputRef.current?.focus()
    },
    [searchOnGeocodedRequest, requireZip]
  )

  const handleSignIn = useCallback(() => {
    onOpen()
    tracker.track(TrackingEvents.STARTED_AUTHENTICATION, {
      source: AuthenticationSource.DeliveryModal,
      isAuthTooltipVisible: false
    })
  }, [onOpen, tracker])

  const keyController = useCallback((event: React.KeyboardEvent) => {
    if (
      !predictionsRef.current ||
      (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')
    ) {
      return
    }
    const focusedElement = predictionsRef.current.querySelector('button:focus')
    let nextElement

    if (inputRef.current === document.activeElement) {
      nextElement = predictionsRef.current.querySelector('button')
    } else if (focusedElement) {
      // get sibling above or below focused element.
      if (event.key === 'ArrowDown') {
        nextElement = focusedElement.nextElementSibling
      } else if (event.key === 'ArrowUp') {
        nextElement = focusedElement.previousElementSibling
      }
    } else {
      return
    }

    if (nextElement instanceof HTMLButtonElement) {
      event.preventDefault()
      nextElement.focus()
    }
  }, [])

  useEffect(() => {
    if (
      placePredictions?.length !== undefined &&
      placePredictions?.length !== null &&
      !isPlacePredictionsLoading &&
      searchText &&
      searchText.length >= MINIMUM_AUTOCOMPLETE_CHARS
    ) {
      announce(
        t(
          placePredictions.length > 0
            ? 'sr-only.results-found'
            : 'sr-only.no-results-found',
          {
            count: placePredictions.length
          }
        )
      )
    }
  }, [announce, isPlacePredictionsLoading, placePredictions, searchText])

  return (
    <div
      className='autocompleteContainer'
      role='search'
      data-testid='inline-places-autocomplete'
    >
      <div className='placesInput'>
        <SearchIcon accessibility='decorative' className='inputSearchIcon' />
        <input
          id={id}
          className='input'
          data-testid='inline-places-autocomplete-input'
          ref={inputRef}
          type='text'
          placeholder={placeholder}
          value={searchText !== undefined ? searchText : defaultValue}
          onChange={(event) => onSearchChange(event.target.value)}
          onKeyDown={keyController}
          aria-controls='locations-list'
          aria-autocomplete='list'
          autoFocus={autoFocus}
        />
        <button
          className='inputClearButton'
          aria-label={t('inline-places-autocomplete.clear-text')}
          onClick={() => setSearchText('')}
        >
          <CloseCircleIcon accessibility='semantic' />
        </button>
      </div>
      {!isPlacePredictionsLoading &&
      (!placePredictions ||
        placePredictions.length === 0 ||
        searchText === '') ? (
        <div className='emptyState'>
          {!shouldShowNoResultsAndManualButton && <SearchIcon />}
          {!customer && !searchText ? (
            <>
              <h3 className='signInPromptTitle'>
                {t('address-hub-screen.faster-checkout-title')}
              </h3>
              <p className='signInPromptBody'>
                {t('address-hub-screen.faster-checkout-body')}
              </p>
              <Button
                as='button'
                className='signInPromptCta'
                onClick={handleSignIn}
                ariaLabel={t('address-hub-screen.sign-in-aria')}
                testId='address-hub-sign-in'
              >
                {t('auth-tooltip.sign-in')}
              </Button>
            </>
          ) : (
            <>
              {shouldShowNoResultsAndManualButton ? (
                <>
                  <h3 className='addressNotFoundTitle'>
                    {t('inline-places-autocomplete.address-not-found')}
                  </h3>
                  <p className='addressNotFoundText'>
                    {t('inline-places-autocomplete.could-not-find-address')}
                  </p>
                  <Button
                    variant={ButtonType.Primary}
                    className='manualAddressTrigger'
                    onClick={() => triggerManualAddress?.(searchText)}
                  >
                    {t('inline-places-autocomplete.manual-address-button')}
                  </Button>
                </>
              ) : (
                helperText
              )}
            </>
          )}
        </div>
      ) : (
        <div className='autocompleteResults'>
          {isPlacePredictionsLoading ? (
            <div className='predictions'>
              {Array.from(Array(6)).map((_, i) => (
                <Skeleton key={i} containerClassName='loadingPrediction' />
              ))}
            </div>
          ) : (
            <>
              <div
                className='predictions'
                ref={predictionsRef}
                id='locations-list'
              >
                {placePredictions.map((prediction) => (
                  <Fragment key={prediction.place_id}>
                    <button
                      className='prediction'
                      onClick={() => onPredictionClick(prediction)}
                      onKeyDown={keyController}
                      tabIndex={0}
                      aria-labelledby={prediction.place_id + '-label'}
                      role='option'
                    >
                      <div
                        id={prediction.place_id + '-label'}
                        className='predictionLabel'
                      >
                        {prediction.structured_formatting.main_text},{' '}
                        {prediction.structured_formatting.secondary_text}
                      </div>
                      <ChevronRightIcon accessibility='decorative' />
                    </button>
                    <div className='divider' aria-hidden='true' />
                  </Fragment>
                ))}
              </div>
              {shouldShowManualButton && (
                <button
                  onClick={() => triggerManualAddress?.(searchText)}
                  className='manualAddressButton'
                >
                  {t('inline-places-autocomplete.manual-address-button')}
                </button>
              )}
            </>
          )}
        </div>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay fadeIn fadeOut />
        <PwlessAuth source={AuthenticationSource.DeliveryModal} />
      </Modal>
    </div>
  )
}

export const ContextualInlinePlacesAutocomplete = (props: Props) => {
  return (
    <A11yProvider>
      <InlinePlacesAutocomplete {...props} />
    </A11yProvider>
  )
}

export default ContextualInlinePlacesAutocomplete
